import { AfterViewInit, ChangeDetectionStrategy, Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { DateRange } from '@angular/material/datepicker';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { isNullOrUndefined } from '@sla-ng/core';
import { ConfirmDialogComponent } from '@sla-ng/ui-material';
import { Moment } from 'moment';
import { BehaviorSubject, distinctUntilChanged, filter, firstValueFrom, Observable, Subject, takeUntil } from 'rxjs';
import { map } from 'rxjs/operators';
import { GeneratePlanningDialogComponent } from '../../../components/generate-planning/generate-planning-dialog.component';
import { PlanningGenerationType } from '../../../models/enum/planning-generation-type.enum';
import { IPlannedPurchasePriceLayout } from '../../../models/layout/planned-purchase-price-layout.model';
import { IPlannedPurchasePrice } from '../../../models/planned-purchase-price.model';
import { IPlanningGenerationSelection } from '../../../models/planning-generation-selection.model';
import { ISharedDataState } from '../../../services/facades/master-data/shared-data-state.model';
import { SharedDataFacade } from '../../../services/facades/master-data/shared-data.facade';
import { IPlannedPurchasePriceState } from '../../../services/facades/planning/planned-purchase-price-state.model';
import { PlannedPurchasePriceFacade } from '../../../services/facades/planning/planned-purchase-price.facade';
import { PlannedPurchasePriceLayoutService } from '../../../services/layout/planned-purchase-price-layout.service';
import { PlannedPurchasePriceDialogComponent } from './planned-purchase-price-dialog/planned-purchase-price-dialog.component';

@Component({
    templateUrl: './planned-purchase-price-page.html',
    styleUrls: [ './planned-purchase-price-page.scss' ],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PlannedPurchasePricePage implements OnInit, AfterViewInit, OnDestroy {

    @ViewChild(MatSort)
    public sort: MatSort;

    @ViewChild(MatPaginator)
    public paginator: MatPaginator;

    public layout: IPlannedPurchasePriceLayout;
    public formGroup$: BehaviorSubject<FormGroup> = new BehaviorSubject<FormGroup>(null);

    public dataSource: MatTableDataSource<IPlannedPurchasePrice> = new MatTableDataSource<IPlannedPurchasePrice>([]);
    public state$: Observable<IPlannedPurchasePriceState>;
    public sharedDataState$: Observable<ISharedDataState>;
    private destroy$: Subject<void> = new Subject();


    constructor(private myPlannedPurchasePriceFacade: PlannedPurchasePriceFacade,
                private mySharedDataFacade: SharedDataFacade,
                private myMatDialog: MatDialog,
                private myPlannedPurchasePriceLayoutService: PlannedPurchasePriceLayoutService) {
    }

    public ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
        this.myPlannedPurchasePriceFacade.clearState();
    }

    public applyFilter(filterValue: string): void {
        this.dataSource.filter = filterValue.trim().toLowerCase();
    }

    public ngOnInit(): void {
        this.layout = this.myPlannedPurchasePriceLayoutService.getPlannedPurchasePriceLayout();
        this.state$ = this.myPlannedPurchasePriceFacade.subState();
        this.sharedDataState$ = this.mySharedDataFacade.subState();
        this.state$
            .pipe(
                takeUntil(this.destroy$),
                map((s: IPlannedPurchasePriceState) => s.data))
            .subscribe((data: IPlannedPurchasePrice[]) => {
                this.dataSource.data = data;
            });
        void this.myPlannedPurchasePriceFacade.fetchData();
        void firstValueFrom((this.mySharedDataFacade.fetchMeasureUnits()));
    }

    public ngAfterViewInit(): void {
        this.dataSource.sort = this.sort;
        this.dataSource.paginator = this.paginator;

        this.sharedDataState$.pipe(
            takeUntil(this.destroy$),
            distinctUntilChanged((prev, curr) =>
                JSON.stringify(prev.articleLookup) === JSON.stringify(curr.articleLookup) &&
                JSON.stringify(prev.measureUnitLookup) === JSON.stringify(curr.measureUnitLookup)),
        ).subscribe((state: ISharedDataState) => {
            this.dataSource.sortingDataAccessor = (item: IPlannedPurchasePrice, tableColumnName: string): string | number => {
                switch (tableColumnName) {
                    case 'measureUnit':
                        return state.measureUnitLookup[ state.articleLookup[ item.articleId ]?.baseMeasureUnitId ]?.name;
                    case 'article':
                        return state.articleLookup[ item.articleId ]?.name;
                    case 'articleNr':
                        return state.articleLookup[ item.articleId ]?.articleNr;
                    default:
                        return item[ tableColumnName ];
                }
            };
        });
    }

    public openAddDialog(plannedPurchasePrice?: IPlannedPurchasePrice): void {
        const filterValues: { articleId: number, dateFilter: DateRange<Moment> } = this.formGroup$.value.getRawValue();
        const dialog: MatDialogRef<PlannedPurchasePriceDialogComponent> = this.myMatDialog.open(PlannedPurchasePriceDialogComponent, {
            width: '600px',
            data: isNullOrUndefined(plannedPurchasePrice) ? { validFrom: filterValues.dateFilter.start, validTo: filterValues.dateFilter.end } : plannedPurchasePrice,
        });
        dialog.afterClosed().subscribe(async () => {
            await this.fetchDataForFilter();
        });
    }

    public openGenerateDialog(): void {
        const dialog: MatDialogRef<GeneratePlanningDialogComponent> = this.myMatDialog.open(GeneratePlanningDialogComponent, {
            data: PlanningGenerationType.PURCHASE_PRICE,
            width: '600px',
        });
        dialog.afterClosed().subscribe(async (generationSelection: IPlanningGenerationSelection) => {
            if (!isNullOrUndefined(generationSelection)) {
                await this.myPlannedPurchasePriceFacade.generatePlannedPrices(generationSelection);
                await this.fetchDataForFilter();
            }
        });
    }

    public onDelete(price: IPlannedPurchasePrice): void {
        const dialog: MatDialogRef<ConfirmDialogComponent> = this.myMatDialog.open(ConfirmDialogComponent, {
            width: '600px',
            data: {
                message: 'app.page.planned-purchase-price.confirm-delete.label',
            },
        });
        dialog.afterClosed().subscribe(async (isConfirmed: boolean) => {
            if (isConfirmed) {
                await this.myPlannedPurchasePriceFacade.deletePlannedPurchasePrice(price);
                await this.fetchDataForFilter();
            }
        });
    }


    public formgroupChange(formGroup: FormGroup): void {
        formGroup.valueChanges.pipe(
            map((formData: { articleId: string | number, dateFilter: DateRange<Moment | string> }) => {
                // Sanitize data: FormGroup may also return empty Strings; Replace them with undefined
                const articleId: number = typeof formData.articleId === 'string' ? undefined : formData.articleId;
                const start: Moment = typeof formData.dateFilter.start === 'string' ? undefined : formData.dateFilter.start;
                const end: Moment = typeof formData.dateFilter.end === 'string' ? undefined : formData.dateFilter.end;

                if (formData.dateFilter.start === '') {
                    formGroup.controls[ 'dateFilter' ].get('start').patchValue(undefined, { emitEvent: false });
                }
                if (formData.dateFilter.end === '') {
                    formGroup.controls[ 'dateFilter' ].get('end').patchValue(undefined, { emitEvent: false });
                }
                return {
                    articleId,
                    dateFilter: new DateRange<Moment>(start, end),
                };
            }),
            filter((v: { articleId: number, dateFilter: DateRange<Moment> }) => this.isDatefilterValid(v.dateFilter.start, v.dateFilter.end)),
            distinctUntilChanged((prev, curr) => JSON.stringify(prev) === JSON.stringify(curr)))
            .subscribe((v: { articleId: number, dateFilter: DateRange<Moment> }) => {
                void this.myPlannedPurchasePriceFacade.fetchPlannedPrices(v.articleId, v.dateFilter.start, v.dateFilter.end);
            },
            );
        this.formGroup$.next(formGroup);
    }

    private async fetchDataForFilter(): Promise<void> {
        const filterValues: { articleId: number, dateFilter: DateRange<Moment> } = this.formGroup$.value.getRawValue();
        await this.myPlannedPurchasePriceFacade.fetchPlannedPrices(filterValues.articleId, filterValues.dateFilter.start, filterValues.dateFilter.end);
    }

    private isDatefilterValid(start: Moment, end: Moment): boolean {
        if (!isNullOrUndefined(start) && !isNullOrUndefined(end) && start.isAfter(end)) {
            return false;
        }
        /* Returns true if either both are undefined or both are set */
        return !((isNullOrUndefined(start) && !isNullOrUndefined(end)) || (isNullOrUndefined(end) && !isNullOrUndefined(start)));
    }
}
